/**
 * SPEC-AgentDetail-Revisions — DecisionFooter.
 *
 * Layout: Good · Bad · | · notify ("CU Notified" / "POD Sent") · | · Skip.
 *   - GOOD / BAD — the existing decision buttons.
 *   - Vertical separator.
 *   - CX Notified (was Notify-CU toggle) — solid orange when on,
 *     outlined when off. Same endpoint contract; visual treatment changed.
 *   - Vertical separator.
 *   - Skip — solid slate button (`#334155`), white text, "⏭ Skip" label.
 *     Wired to a dedicated agent SKIP path on POST /decision.
 *
 * The drop-zone helptext default is gone — Sarah found it noisy.
 *
 * viewMode === 'senior' yields to the senior layout owned by qa-detail-v3
 * during the one-cycle delegation window. A follow-up SPEC will fold the
 * senior dialogs into this file alongside the agent layout.
 */

import type { ViewMode } from "@/hooks/use-view-mode";
import { Starling } from "@/lib/starling-brand";
import { CxNotifiedButton } from "./cx-notified-button";

export interface DecisionFooterProps {
  viewMode: ViewMode;
  customer: string | null | undefined;
  /** qa_deliveries.expected_qatt — picks the notify button's wording (notifyLabel). */
  expectedQatt?: number | null;
  onGood: () => void;
  onBad: () => void;
  onSkip: () => void;
  notifyOn: boolean;
  onNotifyChange: (next: boolean) => void;
  decisionInFlight?: boolean;
  notifyInFlight?: boolean;
  skipInFlight?: boolean;
}

const TOKENS = {
  card: "#FFFFFF",
  border: "#E5E7EB",
  goodCalm: "#166534",
  goodCalmBg: "#DCFCE7",
  goodCalmBorder: "rgba(22,101,52,.25)",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  badBorder: "#FECACA",
  slate: "#334155",
} as const;

export function DecisionFooter({
  viewMode,
  customer,
  expectedQatt,
  onGood,
  onBad,
  onSkip,
  notifyOn,
  onNotifyChange,
  decisionInFlight,
  notifyInFlight,
  skipInFlight,
}: DecisionFooterProps) {
  if (viewMode === "senior") {
    return null; // senior footer rendered by v3 for the one-cycle window
  }
  return (
    <div
      data-testid="agent-detail-decision-footer"
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 8,
        padding: "11px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        boxShadow: "0 -2px 12px rgba(16,24,40,.10)",
        fontFamily: Starling.fontBody,
      }}
    >
      <button
        type="button"
        onClick={onGood}
        disabled={decisionInFlight}
        data-testid="agent-detail-decision-good"
        style={{
          padding: "11px 24px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          border: `1.5px solid ${TOKENS.goodCalmBorder}`,
          background: TOKENS.goodCalmBg,
          color: TOKENS.goodCalm,
          minWidth: 110,
          cursor: decisionInFlight ? "not-allowed" : "pointer",
          opacity: decisionInFlight ? 0.6 : 1,
          fontFamily: Starling.fontBody,
        }}
      >
        ✓ Good
      </button>
      <button
        type="button"
        onClick={onBad}
        disabled={decisionInFlight}
        data-testid="agent-detail-decision-bad"
        style={{
          padding: "11px 24px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          border: `1.5px solid ${TOKENS.badBorder}`,
          background: TOKENS.badBg,
          color: TOKENS.bad,
          minWidth: 110,
          cursor: decisionInFlight ? "not-allowed" : "pointer",
          opacity: decisionInFlight ? 0.6 : 1,
          fontFamily: Starling.fontBody,
        }}
      >
        ✕ Bad
      </button>
      <span
        data-testid="agent-detail-vsep"
        aria-hidden
        style={{ width: 1, height: 32, background: TOKENS.border, margin: "0 6px" }}
      />
      <CxNotifiedButton
        on={notifyOn}
        customer={customer}
        expectedQatt={expectedQatt}
        onChange={onNotifyChange}
        loading={notifyInFlight}
      />
      <span
        data-testid="agent-detail-vsep"
        aria-hidden
        style={{ width: 1, height: 32, background: TOKENS.border, margin: "0 6px" }}
      />
      <button
        type="button"
        onClick={onSkip}
        disabled={skipInFlight || decisionInFlight}
        data-testid="agent-detail-decision-skip"
        style={{
          background: TOKENS.slate,
          color: "#fff",
          border: `1.5px solid ${TOKENS.slate}`,
          padding: "11px 18px",
          borderRadius: 8,
          fontSize: 13.5,
          fontWeight: 700,
          minWidth: 96,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          cursor: skipInFlight ? "not-allowed" : "pointer",
          opacity: skipInFlight || decisionInFlight ? 0.6 : 1,
          fontFamily: Starling.fontBody,
        }}
      >
        ⏭ Skip
      </button>
    </div>
  );
}

export default DecisionFooter;
